import Seo from "@/components/Seo";
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Mail, Megaphone, ArrowRight } from "lucide-react";
import { PageHeader } from "@/components/Shared";
import { useConfig } from "@/lib/useConfig";

const PACKAGES = [
  {
    key: "newsletter", icon: Mail, name: "Newsletter Sponsorship", price: 49, unit: "per issue",
    tagline: "Reach our growing AI community in the WorldAI Weekly newsletter.",
    features: ["Dedicated sponsor slot in one issue", "Logo + 50-word blurb + link", "Sent to our newsletter community", "Performance summary after send"],
  },
  {
    key: "advertiser", icon: Megaphone, name: "Monthly Advertiser Package", price: 99, unit: "per month", popular: true,
    tagline: "Consistent brand visibility across the platform for a full month.",
    features: ["Banner placement across key pages", "Featured in one newsletter issue", "Priority listing consideration", "Monthly performance report"],
  },
];

export default function Advertise() {
  const { paymentsEnabled } = useConfig();
  return (
    <div data-testid="advertise-page">
      <Seo title="Advertise on WorldAIInfo — Reach the AI Community" description="Promote your product or brand to a growing global community of AI professionals, builders and decision-makers." canonicalPath="/advertise" />
      <PageHeader overline="Partner With Us" title="Advertise on WorldAIInfo"
        subtitle="Put your brand in front of our growing AI community of founders, engineers and decision-makers." />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PACKAGES.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div key={p.key} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`rounded-2xl p-7 border flex flex-col ${p.popular ? "border-[#00F0FF]/50 bg-[#00F0FF]/[0.05]" : "border-white/10 bg-[#0e1424]"}`}
                data-testid={`advertise-package-${p.key}`}>
                <div className="w-11 h-11 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <h3 className="font-display font-bold text-xl">{p.name}</h3>
                <p className="text-sm text-[#94A3B8] mt-1 mb-4">{p.tagline}</p>
                {paymentsEnabled ? (
                  <div className="flex items-end gap-1 mb-5">
                    <span className="font-display font-extrabold text-4xl text-[#00F0FF]">${p.price}</span>
                    <span className="text-[#94A3B8] text-sm mb-1.5">{p.unit}</span>
                  </div>
                ) : (
                  <div className="flex items-end gap-2 mb-5">
                    <span className="font-display font-extrabold text-4xl text-[#00F0FF]">Free</span>
                    <span className="text-[#94A3B8] text-sm mb-1.5">while we grow</span>
                  </div>
                )}
                <ul className="space-y-2.5 text-sm text-[#94A3B8] flex-1 mb-6">
                  {p.features.map((f, idx) => (
                    <li key={idx} className="flex gap-2"><Check className="w-4 h-4 text-[#22C55E] shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
                <Link to="/contact" data-testid={`advertise-contact-${p.key}`}
                  className={`w-full inline-flex items-center justify-center gap-1.5 font-semibold px-5 py-3 rounded-md transition text-sm ${p.popular ? "bg-[#00F0FF] text-[#04121a] hover:brightness-110" : "border border-white/15 hover:border-[#00F0FF]/60"}`}>
                  Contact Us <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            );
          })}
        </div>
        <p className="text-center text-xs text-[#64748B] mt-8 max-w-2xl mx-auto">
          Interested in a custom partnership or bulk placement? <Link to="/contact" className="text-[#00F0FF] hover:underline">Get in touch</Link> and
          our team will tailor a package for you.
        </p>
      </div>
    </div>
  );
}
